import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { createBatchedWriter, storagePathFromUrl } from '@/lib/cascade';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Deal, DealInput } from '@elka/types';

async function fetchDocsByDealId(
  collectionPath: string,
  dealId: string
): Promise<{ id: string; data: Record<string, unknown> }[]> {
  const snap = await getDocs(
    query(collection(db, ...splitPath(collectionPath)), where('dealId', '==', dealId))
  );
  return snap.docs.map((d) => ({ id: d.id, data: d.data() }));
}

export function useDeals() {
  const { user } = useAuth();

  // Query pattern same as EM's useClients - unconditional.
  // Memoized so the query reference is stable across renders — otherwise a fresh
  // query() object every render churns useCollection's subscription.
  const dealsQuery = useMemo(
    () => query(collection(db, ...splitPath(COLLECTIONS.crm.deals)), orderBy('createdAt', 'desc')),
    []
  );

  const [snapshot, loading, error] = useCollection(dealsQuery);

  const deals: Deal[] =
    (snapshot?.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Deal[]) || [];

  // Add deal
  const addDeal = async (data: DealInput) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const userInfo = {
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: timestamp,
    };

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.crm.deals)), {
      ...data,
      ...userInfo,
    });

    return docRef.id;
  };

  // Update deal
  const updateDeal = async (id: string, data: Partial<DealInput>) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const updateInfo = {
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: timestamp,
    };

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.crm.deals), id), {
      ...data,
      ...updateInfo,
    });

    // Deal rename: the quotes app denormalizes the deal name as projectName on
    // its docs (and into quote PDFs), tickets as dealName — fan the new name out so
    // they don't go stale. ponytail: single writeBatch, a deal links to far fewer than 500 docs.
    const existing = deals.find((d) => d.id === id);
    const oldName = existing?.name;
    const renamed = !!(data.name && oldName && oldName !== data.name);
    if (renamed) {
      const quoteCollections = [
        COLLECTIONS.quotes.quotes,
        COLLECTIONS.quotes.costEstimates,
        COLLECTIONS.quotes.bidRequests,
        COLLECTIONS.quotes.bills,
      ];
      const batch = writeBatch(db);
      for (const collectionPath of quoteCollections) {
        const docs = await getDocs(
          query(collection(db, ...splitPath(collectionPath)), where('dealId', '==', id))
        );
        for (const d of docs.docs) {
          // projectName is a user-editable label that only DEFAULTS to the deal
          // name at creation. Only re-sync docs still carrying the old deal name;
          // skip any the user customized so a rename can't clobber their label.
          if (d.data().projectName !== oldName) continue;
          // A quote that left 'draft' was already sent to the client — frozen, rename or not.
          if (collectionPath === COLLECTIONS.quotes.quotes && d.data().status !== 'draft') continue;
          batch.update(doc(db, ...splitPath(collectionPath), d.id), {
            projectName: data.name,
            ...updateInfo,
          });
        }
      }
      // Tasks-app tickets denormalize the deal name as dealName; always kept in sync.
      const tickets = await getDocs(
        query(collection(db, ...splitPath(COLLECTIONS.tasks.tickets)), where('dealId', '==', id))
      );
      for (const t of tickets.docs) {
        batch.update(doc(db, ...splitPath(COLLECTIONS.tasks.tickets), t.id), {
          dealName: data.name,
        });
      }
      await batch.commit();
    }
  };

  // Delete deal — cascade the deal-scoped collections so a single-deal delete
  // doesn't orphan its comments/documents (and their Storage files), activities
  // and tasks, mirroring deleteCompany's cascade. ponytail: quotes-app docs
  // (quotes/estimates/bids/bills) are intentionally left intact — they outlive
  // the deal and are managed by their own app.
  const deleteDeal = async (id: string) => {
    if (!user) throw new Error('לא מחובר');

    const commentDocs = await fetchDocsByDealId(COLLECTIONS.crm.dealComments, id);
    const documentDocs = await fetchDocsByDealId(COLLECTIONS.crm.dealDocuments, id);
    const activityDocs = await fetchDocsByDealId(COLLECTIONS.crm.activities, id);
    const taskDocs = await fetchDocsByDealId(COLLECTIONS.crm.tasks, id);

    const writer = createBatchedWriter();
    for (const c of commentDocs)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.dealComments), c.id));
    for (const d of documentDocs)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.dealDocuments), d.id));
    for (const a of activityDocs)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.activities), a.id));
    for (const t of taskDocs) writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.tasks), t.id));
    writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.deals), id));
    await writer.commit();

    // Best-effort Storage cleanup for deal documents — Firestore is the source of
    // truth, so a missing file doesn't roll back the cascade. Same URL→path
    // extraction as deleteCompany.
    await Promise.all(
      documentDocs.map(async (d) => {
        const fileUrl = (d.data as { fileUrl?: string }).fileUrl;
        if (!fileUrl) return;
        const path = storagePathFromUrl(fileUrl);
        try {
          await deleteObject(storageRef(storage, path));
        } catch (err) {
          console.warn('[deleteDeal] failed to delete storage file:', path, err);
        }
      })
    );
  };

  return {
    deals,
    isLoading: loading,
    error: error?.message || null,
    addDeal,
    updateDeal,
    deleteDeal,
  };
}
